import { DeleteIcon } from '@assets/icons';
import { Group } from '@components/layout';
import { useConfirmation } from '@components/modal';
import { Typography } from '@components/typography';
import { useDeleteEditorMutation, type Editor } from '@generated/graphql';
import { MenuItem } from '@mui/material';
import { useNotifications } from '@notifications/Notifications';
import posthog from 'posthog-js';
import type { MouseEventHandler } from 'react';

type DeleteServerOptionProps = {
  server: Pick<Editor, 'editorId' | 'workspaceId'>;
  onClick: MouseEventHandler;
  onDelete?: () => void;
};

export const DeleteServerOption = ({ onClick, server, onDelete }: DeleteServerOptionProps) => {
  const { editorId, workspaceId } = server;

  const { showConfirmation } = useConfirmation();
  const { createAsyncNotification } = useNotifications();
  const [deleteServerMutation] = useDeleteEditorMutation();

  const deleteServer = async () => {
    posthog.capture('agent-studio:user_delete-server_submitted', {
      workspaceId,
      editorId,
    });
    await deleteServerMutation({
      variables: { editorId },
      update(cache) {
        cache.evict({ id: cache.identify(server) });
        cache.gc();
      },
      onCompleted: () => {
        posthog.capture('agent-studio:user_delete-server_completed', {
          workspaceId,
          editorId,
        });
        onDelete?.();
      },
      onError: (error) => {
        posthog.capture('agent-studio:user_delete-server_failed', {
          workspaceId,
          editorId,
          error: (error as Error)?.message || 'An error occurred during deletion',
        });
        throw error;
      },
    });
  };

  const handleServerDelete: MouseEventHandler = (event) => {
    onClick?.(event);
    showConfirmation({
      onAffirm: createAsyncNotification('Server deleted.', deleteServer, {
        loadingMessage: 'Deleting server...',
      }),
      message: `Are you sure you want to delete the server: ${editorId}?`,
    });
  };

  return (
    <MenuItem onClick={handleServerDelete}>
      <Group alignItems="center" gap={2}>
        <DeleteIcon size={14} />
        <Typography variant="caption2">Delete</Typography>
      </Group>
    </MenuItem>
  );
};
